import { differenceInMonths, differenceInYears, format, parseISO } from 'date-fns'

export function onlyDigits(value: string): string {
  return value.replace(/\D+/g, '')
}

export function formatCpf(cpf: string | null | undefined): string {
  const digits = onlyDigits(cpf ?? '')
  if (digits.length !== 11) return cpf ?? ''
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

/** Mantém só A-Z/0-9 em maiúsculas — o CNPJ alfanumérico (2026) aceita letras nas 12 primeiras posições. */
export function normalizeCnpj(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '')
}

export function formatCnpj(cnpj: string | null | undefined): string {
  const value = normalizeCnpj(cnpj ?? '')
  if (value.length !== 14) return cnpj ?? ''
  return value.replace(/(\w{2})(\w{3})(\w{3})(\w{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

/** CPF (11 dígitos) ou CNPJ (14 caracteres), conforme o tamanho; outro formato volta como veio. */
export function formatCpfCnpj(value: string | null | undefined): string {
  const normalized = normalizeCnpj(value ?? '')
  if (normalized.length === 11) return formatCpf(normalized)
  if (normalized.length === 14) return formatCnpj(normalized)
  return value ?? ''
}

export function formatPhone(phone: string | null | undefined): string {
  const digits = onlyDigits(phone ?? '')
  if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  return phone ?? ''
}

export function formatCep(cep: string | null | undefined): string {
  const digits = onlyDigits(cep ?? '')
  if (digits.length !== 8) return cep ?? ''
  return digits.replace(/(\d{5})(\d{3})/, '$1-$2')
}

/** Aceita "yyyy-MM-dd" ou ISO completo; devolve "dd/MM/yyyy". */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return format(parseISO(value), 'dd/MM/yyyy')
  } catch {
    return value
  }
}

/** ISO completo (com hora) -> "dd/MM/yyyy HH:mm". */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return format(parseISO(value), 'dd/MM/yyyy HH:mm')
  } catch {
    return value
  }
}

/** Máscara de valor oculto — tamanho fixo para não revelar a ordem de grandeza. */
export const VALOR_OCULTO = 'R$ ••••'

export function formatCurrency(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

/** Versão curta para eixos de gráfico: 12345 -> "R$ 12,3 mil". */
export function formatCurrencyCompact(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

const MONTH_SHORT_FORMAT = new Intl.DateTimeFormat('pt-BR', { month: 'short' })

/** Competência "YYYY-MM" -> "mar/26". Devolve o valor original se o formato não casar. */
export function formatMonthLabel(value: string | null | undefined): string {
  if (!value) return '—'
  const match = /^(\d{4})-(\d{2})$/.exec(value)
  if (!match) return value
  const [, year, month] = match
  const label = MONTH_SHORT_FORMAT.format(new Date(Number(year), Number(month) - 1, 1)).replace(
    '.',
    '',
  )
  return `${label}/${year.slice(2)}`
}

export function calculateAge(birthDate: string | null | undefined): number | null {
  if (!birthDate) return null
  try {
    return differenceInYears(new Date(), parseISO(birthDate))
  } catch {
    return null
  }
}

export function formatAge(birthDate: string | null | undefined): string {
  const age = calculateAge(birthDate)
  return age == null ? '—' : `${age} anos`
}

/** Tempo decorrido desde `dateISO` como "X anos e Y meses" / "Z meses" / "menos de 1 mês". */
export function formatTenure(dateISO: string | null | undefined): string {
  if (!dateISO) return '—'
  try {
    const start = parseISO(dateISO)
    const totalMonths = Math.max(0, differenceInMonths(new Date(), start))
    const years = Math.floor(totalMonths / 12)
    const months = totalMonths % 12
    if (totalMonths === 0) return 'menos de 1 mês'
    const parts: string[] = []
    if (years > 0) parts.push(`${years} ${years === 1 ? 'ano' : 'anos'}`)
    if (months > 0) parts.push(`${months} ${months === 1 ? 'mês' : 'meses'}`)
    return parts.join(' e ')
  } catch {
    return '—'
  }
}

export function getInitials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function greetingForHour(date = new Date()): 'Bom dia' | 'Boa tarde' | 'Boa noite' {
  const hour = date.getHours()
  if (hour < 12) return 'Bom dia'
  if (hour < 18) return 'Boa tarde'
  return 'Boa noite'
}

/** ISO -> tempo relativo curto ("agora", "há 5 min", "há 3 h", "ontem", "há 4 dias"); depois de 7 dias, a data. */
export function formatRelativeTime(value: string | null | undefined, now = new Date()): string {
  if (!value) return '—'
  const date = parseISO(value)
  if (Number.isNaN(date.getTime())) return value

  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000)
  if (minutes < 1) return 'agora'
  if (minutes < 60) return `há ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `há ${hours} h`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'ontem'
  if (days < 7) return `há ${days} dias`
  return formatDate(value)
}
